import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface BackToTopProps {
  showAfter?: number; // Kaç pixel scroll'dan sonra gösterilecek
  className?: string; // Ek CSS class'ları
  onVisibilityChange?: (isVisible: boolean) => void; // Görünürlük değişikliği callback'i
}

const BackToTop: React.FC<BackToTopProps> = ({ 
  showAfter = 300, 
  className = '',
  onVisibilityChange
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isFirstShow, setIsFirstShow] = useState(true);

  // Scroll pozisyonunu takip et
  useEffect(() => {
    const toggleVisibility = () => {
      const st = window.pageYOffset > showAfter;
      const isModalOpen = document.body.hasAttribute('data-modal-open');
      
      // Modal açıksa back-to-top'u gizle (hem desktop hem mobil için)
      const shouldBeVisible = st && !isModalOpen;
      const shouldNotifyParent = st; // Parent'a scroll pozisyonuna göre bildir
      
      if (shouldBeVisible !== isVisible) {
        setIsVisible(shouldBeVisible);
        
        if (shouldBeVisible && isFirstShow) {
          setIsFirstShow(false);
        }
      }
      
      // Parent'a bildirim - modal durumundan bağımsız olarak scroll pozisyonuna göre
      if (onVisibilityChange) {
        onVisibilityChange(shouldNotifyParent);
      }
    };

    // Scroll event listener'ı ekle
    window.addEventListener('scroll', toggleVisibility);
    
    // Body attribute değişikliklerini izle (modal açılıp kapanması için)
    const observer = new MutationObserver(toggleVisibility);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['style', 'data-modal-open']
    });
    
    // Window resize listener'ı ekle (responsive davranış için)
    window.addEventListener('resize', toggleVisibility);
    
    // İlk kontrol
    toggleVisibility();

    // Cleanup
    return () => {
      window.removeEventListener('scroll', toggleVisibility);
      window.removeEventListener('resize', toggleVisibility);
      observer.disconnect();
    };
  }, [showAfter, isVisible, isFirstShow, onVisibilityChange]);

  // Yukarı kaydır
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    
    // Smooth scroll bittiğinde scroll event'ini manuel tetikle
    // Bu, sayfa en üste geldiğinde menü düğmesinin gösterilmesini sağlar
    setTimeout(() => {
      window.dispatchEvent(new Event('scroll'));
    }, 100);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.button
          onClick={scrollToTop}
          initial={{ 
            opacity: 0, 
            scale: 0.3,
            y: 30,
            rotate: -180
          }}
          animate={{ 
            opacity: 1, 
            scale: isFirstShow ? [1, 1.2, 1] : 1,
            y: 0,
            rotate: 0
          }}
          exit={{ 
            opacity: 0, 
            scale: 0.3,
            y: 30,
            rotate: 180
          }}
          whileHover={{ 
            scale: 1.15,
            y: -4,
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
          }}
          whileTap={{ 
            scale: 0.9,
            y: -2
          }}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 20,
            duration: 0.4
          }}
          className={`
            fixed bottom-6 right-6 z-50
            w-12 h-12
            text-white
            bg-gradient-to-r from-emerald-600 to-emerald-700
            hover:from-emerald-700 hover:to-emerald-800
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500
            rounded-full shadow-lg shadow-emerald-500/25
            hover:shadow-xl hover:shadow-emerald-500/30
            flex items-center justify-center
            group
            ${className}
          `}
          aria-label="Yukarı git"
        >
          <motion.svg 
            className="w-6 h-6" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
            initial={{ 
              y: 0,
              rotate: 0
            }}
            whileHover={{
              y: -2,
              rotate: -5
            }}
            transition={{
              type: "spring",
              stiffness: 500,
              damping: 30
            }}
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth="2" 
              d="M5 10l7-7m0 0l7 7m-7-7v18" 
            />
          </motion.svg>
        </motion.button>
      )}
    </AnimatePresence>
  );
};

export default BackToTop; 